import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import EmploymentInput from '../../../../components/Guarantor/EmploymentInput';
import LabeledInput from '../../../../components/LabeledInput';
import MailingAddressInput from '../../../../components/Guarantor/MailingAddressInput';
import PhoneInput from '../../../../components/PhoneInput';
import RelationshipPicker from '../../../../components/Guarantor/RelationshipPicker';
import { DatePicker } from '../../../../components/DatePicker';
import { isEnumMember } from 'common/dist/utils/types';
import SameAsPatientOrCustom from '../../../../components/SameAsPatientOrCustom';
import { SelectInput } from '../../../../components/SelectInput';
import { TextInput } from '../../../../components/TextInput';

import * as api from '../../../../api';

import defaultStyles from '../../../../styles/formSetStyles.module.css';
import guarantorStyles from '../../../../components/Guarantor/index.module.css';

type Subscriber = api.PrevisitPatientInsurance.PrevisitSubscriber;

interface Props extends WithTranslation {
  selectedSubscriber?: Subscriber;
  onChange: (values: Partial<Subscriber>) => void;
  employmentStatusOptions: api.PracticePreference.EmploymentStatus[];
  patientRelationshipsToOptions: api.PracticePreference.RelationshipPreference;
  collectRetirementDate: boolean;
  helpDialog: string | null;
  patientAddress: api.Address.Address | null;
  patientCellPhoneNumber?: string;
}

class SubscriberInfo extends React.Component<Props> {
  renderForm() {
    const {
      collectRetirementDate,
      selectedSubscriber,
      employmentStatusOptions,
      patientRelationshipsToOptions,
      patientAddress,
      onChange,
      patientCellPhoneNumber,
      t,
    } = this.props;

    if (
      !selectedSubscriber ||
      selectedSubscriber.patientRelationshipToPrimaryPolicyholder ===
        api.Common.Relationship.Self
    ) {
      return null;
    }

    if (!selectedSubscriber.newlyAdded) {
      const contactName = [
        selectedSubscriber.primaryPolicyHolderFirstName,
        selectedSubscriber.primaryPolicyHolderLastName,
      ].join(' ');
      return (
        <>
          <div>
            <label className={defaultStyles.Label}>
              {`Review ${contactName}'s address on file and update if changed`}
            </label>
            <MailingAddressInput
              onChange={(primaryPolicyHolderAddress) =>
                onChange({ primaryPolicyHolderAddress })
              }
              value={selectedSubscriber.primaryPolicyHolderAddress ?? null}
              patientAddress={patientAddress ?? null}
            />
          </div>
          <div>
            <RelationshipPicker
              onChange={(relationship) => {
                onChange({
                  patientRelationshipToPrimaryPolicyholder: relationship,
                });
              }}
              relationshipOptions={patientRelationshipsToOptions}
              value={
                selectedSubscriber.patientRelationshipToPrimaryPolicyholder
              }
              associatedEntity={
                contactName ?? 'the subscriber of the insurance'
              }
            />
          </div>
        </>
      );
    }
    const label = t(`Add your subscriber's information`);

    return (
      <div className={guarantorStyles.ResponsibleContactInfo}>
        <label className={defaultStyles.Label}>{label}</label>
        <LabeledInput label={t('firstName')} htmlFor="subscriber-first-name">
          <TextInput
            id="subscriber-first-name"
            placeholder={t('firstName')}
            value={selectedSubscriber.primaryPolicyHolderFirstName || ''}
            onChange={(e) =>
              onChange({ primaryPolicyHolderFirstName: e.target.value })
            }
          />
        </LabeledInput>

        <LabeledInput label={t('lastName')} htmlFor="subscriber-last-name">
          <TextInput
            id="subscriber-last-name"
            placeholder={t('lastName')}
            value={selectedSubscriber.primaryPolicyHolderLastName || ''}
            onChange={(e) =>
              onChange({ primaryPolicyHolderLastName: e.target.value })
            }
          />
        </LabeledInput>

        <LabeledInput label={t('Sex')} htmlFor="subscriber-sex">
          <SelectInput
            id="subscriber-sex"
            onChange={(primaryPolicyHolderSex: string) => {
              if (!isEnumMember(api.Previsit.Sex, primaryPolicyHolderSex)) {
                return;
              }
              onChange({ primaryPolicyHolderSex });
            }}
            options={api.Previsit.DefaultSexes.map((sex) => ({
              display: api.Previsit.sexOptionsMap[sex],
              key: sex,
              value: sex,
            }))}
            value={selectedSubscriber.primaryPolicyHolderSex?.toString() || ''}
          />
        </LabeledInput>

        <LabeledInput label={t('Date of Birth')} htmlFor="subscriber-dob">
          <DatePicker
            curvedInput={5}
            date={
              selectedSubscriber.primaryPolicyHolderDob &&
              !isNaN(
                new Date(selectedSubscriber.primaryPolicyHolderDob).valueOf(),
              )
                ? new Date(
                    selectedSubscriber.primaryPolicyHolderDob,
                  ).toISOString()
                : ''
            }
            onChange={(primaryPolicyHolderDob) =>
              onChange({ primaryPolicyHolderDob })
            }
          />
        </LabeledInput>

        <MailingAddressInput
          onChange={(primaryPolicyHolderAddress) =>
            onChange({ primaryPolicyHolderAddress })
          }
          value={selectedSubscriber.primaryPolicyHolderAddress ?? null}
          patientAddress={patientAddress ?? null}
        />

        <LabeledInput
          label={t('phoneNumber')}
          htmlFor="subscriber-phone-number"
        >
          <SameAsPatientOrCustom
            customInputRender={(isDisabled) => (
              <PhoneInput
                label={t('phoneNumber')}
                id="subscriber-phone-number"
                value={selectedSubscriber.primaryPolicyHolderHomePhone || ''}
                updateFieldValue={(primaryPolicyHolderHomePhone) =>
                  onChange({ primaryPolicyHolderHomePhone })
                }
                saveFieldValue={(primaryPolicyHolderHomePhone) =>
                  onChange({ primaryPolicyHolderHomePhone })
                }
                disabled={isDisabled}
              />
            )}
            customElementName="phone number"
            value={selectedSubscriber.primaryPolicyHolderHomePhone}
            patientValue={patientCellPhoneNumber || ''}
            onSameAsPatientClicked={() => {
              onChange({
                primaryPolicyHolderHomePhone: patientCellPhoneNumber,
              });
            }}
          />
        </LabeledInput>

        <EmploymentInput
          retirementDate={selectedSubscriber.primaryPolicyHolderRetirementDate}
          employmentStatus={
            selectedSubscriber.primaryPolicyHolderEmploymentStatus
          }
          employer={selectedSubscriber.primaryPolicyHolderEmployer ?? null}
          employmentStatusOptions={employmentStatusOptions}
          collectRetirementDate={collectRetirementDate ?? false}
          onChange={({ employer, retirementDate, employmentStatus }) =>
            onChange({
              ...(employer !== undefined && {
                primaryPolicyHolderEmployer: employer,
              }),
              ...(retirementDate !== undefined && {
                primaryPolicyHolderRetirementDate: retirementDate,
              }),
              ...(employmentStatus !== undefined && {
                primaryPolicyHolderEmploymentStatus: employmentStatus,
                primaryPolicyHolderEmploymentStatusRaw: employmentStatus,
              }),
            })
          }
          onSave={({ employer, retirementDate, employmentStatus }) =>
            onChange({
              ...(employer !== undefined && {
                primaryPolicyHolderEmployer: employer,
              }),
              ...(retirementDate !== undefined && {
                primaryPolicyHolderRetirementDate: retirementDate,
              }),
              ...(employmentStatus !== undefined && {
                primaryPolicyHolderEmploymentStatus: employmentStatus,
                primaryPolicyHolderEmploymentStatusRaw: employmentStatus,
              }),
            })
          }
        />

        <RelationshipPicker
          onChange={(patientRelationshipToPrimaryPolicyholder) => {
            onChange({ patientRelationshipToPrimaryPolicyholder });
          }}
          relationshipOptions={patientRelationshipsToOptions}
          value={selectedSubscriber.patientRelationshipToPrimaryPolicyholder}
          associatedEntity="the subscriber of the insurance"
        />
      </div>
    );
  }

  render() {
    return (
      <div>
        <div>{this.renderForm()}</div>
      </div>
    );
  }
}

export default withTranslation()(SubscriberInfo);
